import { useCallback, useState } from 'react';

import SearchBar from 'components/tables/list-table/filter-pieces/SearchBar';
import { Skeleton } from 'components/ui/skeleton';

// Third-party
import { Link as RouterLink } from 'react-router-dom';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { t } from 'utils/getTranslations';

// Project imports
import DmsLink from 'components/dmslink/DmsLink';
import LineClamp from 'components/misc/LineClamp';
import { Button } from 'components/ui/button';
import { GavelOutlined as GavelOutlinedIcon } from 'components/icons';
import EmptyState from 'components/EmptyState';
import PartyName from 'components/party/PartyName';
import LoadMoreButton from 'components/tables/LoadMoreButton';
import Unmasked from 'components/Unmasked';
import { useLoadMoreFeed } from 'hooks/useLoadMoreFeed';
import { FormattedMessage } from 'react-intl';
import { useLazyGetDashboardMattersWithFiltersQuery } from 'store/api/matters';
import { warnIfKeywordsTruncated } from 'utils/multiKeywordSearch';
import { buildRsqlFromSearchText } from 'utils/searchQueryToRsql';
import MatterIndicators, { hasMatterAccess } from 'components/matters/MatterIndicators';
import { MatterListItem, MatterVisibilityInfo } from 'types/matters';
import { cn } from 'utils/cn';
import BlockedTag from '../timecards/buttons/BlockedTag.tsx';
import { useClosableMatterIds } from 'pages/matters/closing/CloseCaseMenuItem';
import MyCaseActionButtons from './MyCaseActionButtons';

const NO_CASES: MatterListItem[] = [];

interface CasesTabProps {
    query: typeof useLazyGetDashboardMattersWithFiltersQuery;
    sort?: string;
    /** Empty-state copy + analytics label. Defaults to the "my cases" wording. */
    emptyTitleId?: string;
    emptyBodyId?: string;
    emptySurface?: string;
    /**
     * Home's list: its section's heading row holds the search, so the parent owns it; the rows are the dense ones
     * and Show more closes the list.
     */
    home?: { search: string; onClearSearch: () => void };
}

const PAGE_SIZE = 10;

const DASHBOARD_SEARCH_FIELDS = [
    'name',
    'description',
    'caseNumber',
    'responsibleEmployee.firstName',
    'responsibleEmployee.lastName',
    'supervisedByEmployee.firstName',
    'supervisedByEmployee.lastName',
    'handledByOffice.name',
    'clientParty.name',
];

const DASHBOARD_SEARCH_SELECTORS = DASHBOARD_SEARCH_FIELDS.map((selector) => ({ selector, comparator: '=lk=' }));

/** A case row, and the skeleton row standing in for one: the last row drops its rule and its bottom padding. */
const CASE_ROW = 'border-divider flex border-b p-5 last:border-b-0 last:pb-0';

/**
 * What a dashboard row shows in its trailing slot. A blocked case never offers time or expense entry,
 * whether or not the viewer can open it; the tag itself is what says nothing on a case they cannot open,
 * so a blocked case the viewer has no access to shows neither.
 */
export const dashboardRowTrailing = (
    matter: Pick<MatterListItem, 'isCurrentlyBlocked'> & MatterVisibilityInfo
): 'actions' | 'blocked-tag' | 'none' => {
    if (!matter.isCurrentlyBlocked) {
        return 'actions';
    }
    return hasMatterAccess(matter) ? 'blocked-tag' : 'none';
};

const CasesTab = (props: CasesTabProps) => {
    const [searchText, setSearchText] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [fetchData, { currentData, isFetching, isError }] = props.query();
    const { home } = props;
    const search = home ? home.search : appliedSearch;

    const fetchPage = useCallback(
        (page: number) => {
            const filter = search ? buildRsqlFromSearchText(search.trim(), DASHBOARD_SEARCH_SELECTORS) : '';
            return fetchData({
                pageSize: PAGE_SIZE,
                page,
                sort: props.sort === undefined ? 'openedDate,desc' : props.sort,
                filters: filter,
            });
        },
        [fetchData, search, props.sort]
    );

    const { handleLoadMore } = useLoadMoreFeed({ fetchPage, isLast: currentData?.last });

    const onSearchInputChange = useDebouncedCallback((value: string) => {
        warnIfKeywordsTruncated(value);
        setAppliedSearch(value);
    }, 600);

    const {
        emptyTitleId = 'dashboard.empty.cases-title',
        emptyBodyId = 'dashboard.empty.cases-body',
        emptySurface = 'dashboard-my-cases',
    } = props;
    // A zero-row response to a search is "no matches", not "no cases" — and it needs a way back.
    const isSearching = search.trim().length > 0;
    const handleClearSearch = () => {
        if (home) {
            home.onClearSearch();
            return;
        }
        setSearchText('');
        // Supersede any keystroke still sitting in the debounce: `useDebouncedCallback` clears its
        // pending timeout on every call, so passing '' replaces the queued value instead of letting
        // it fire 600ms later and restore `appliedSearch` behind an already-emptied search box.
        onSearchInputChange('');
        setAppliedSearch('');
    };
    const handleRetry = () => {
        void fetchPage(0);
    };

    const casesCount = currentData?.totalElements || 0;
    const closableMatterIds = useClosableMatterIds(currentData?.content ?? NO_CASES);

    return (
        <div className={home ? undefined : 'mb-5 pt-4'}>
            {home ? null : (
                <>
                    <SearchBar
                        value={searchText}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            onSearchInputChange(e.target.value);
                        }}
                        placeHolder={t('search')}
                        className="mx-5 w-[calc(100%-40px)]"
                        showSearchTips
                    />
                    {/* No count until this search has one, and a blank line meanwhile so the rows do not jump. */}
                    <p className="text-body1 px-5 pt-1.5 pb-2.5">
                        {currentData ? (
                            <FormattedMessage
                                id="dashboard.n-cases"
                                values={{
                                    count: casesCount,
                                    element: (
                                        <span key="element" className="font-semibold">
                                            {casesCount}
                                        </span>
                                    ),
                                }}
                            />
                        ) : (
                            <>&nbsp;</>
                        )}
                    </p>
                </>
            )}
            {/* Order matters: a failed request and a request still in flight must NOT read as
                "there are no cases". `currentData` holds this search's rows only (RTK's `data` keeps
                the previous search's), so a new search draws the skeleton and a failed one the error,
                while Load more, which shares the search's cache entry, keeps the rows. `!currentData`
                also covers the frame before the mount effect fires the lazy query, where isFetching
                is still false. */}
            {!currentData?.content.length && isError ? (
                <EmptyState size="panel" variant="error" onRetry={handleRetry} surface={emptySurface} />
            ) : !currentData?.content.length && !isFetching && currentData ? (
                <EmptyState
                    size="panel"
                    variant={isSearching ? 'filtered' : 'empty'}
                    icon={GavelOutlinedIcon}
                    titleId={emptyTitleId}
                    bodyId={emptyBodyId}
                    surface={emptySurface}
                    onClearFilters={handleClearSearch}
                />
            ) : null}
            {(isFetching || !currentData) && !currentData?.content.length && !isError ? (
                <GridLoader />
            ) : (
                currentData?.content.map((matter, index) => {
                    const canCloseCase = closableMatterIds.has(matter.id);
                    if (home) {
                        return (
                            <HomeCaseRow key={`my-matter-${matter.id}`} matter={matter} canCloseCase={canCloseCase} />
                        );
                    }
                    return (
                        <div
                            className={cn(CASE_ROW, 'flex-wrap', index === 0 && 'mt-1 border-t')}
                            key={`my-matter-${matter.id}`}
                        >
                            <div className="flex min-w-0 flex-1 flex-col">
                                {/* min-w-0 on both: the icon row makes the link a flex item, whose
                                    default min-width:auto would stop the title's break-word wrap. */}
                                <div className="flex min-w-0 items-center gap-1">
                                    <MatterIndicators
                                        isConfidential={matter.isConfidential}
                                        hasAccess={hasMatterAccess(matter)}
                                    />
                                    <RouterLink
                                        to={`/matter/${matter.id}`}
                                        className="text-fg hover:text-link w-fit min-w-0 no-underline hover:underline"
                                    >
                                        <h5 className="text-h5 text-balance break-words">
                                            <span className="font-bold">
                                                <Unmasked>{matter.caseNumber + ' '}</Unmasked>
                                            </span>
                                            <span className="font-medium">{matter.name}</span>
                                        </h5>
                                    </RouterLink>
                                </div>
                                {matter.clientParty?.id ? (
                                    <h5 className="text-h5 font-medium text-balance break-words">
                                        <PartyName
                                            party={matter.clientParty}
                                            isLink
                                            variant="readonly-other"
                                            showPartyNumber
                                        />
                                    </h5>
                                ) : null}
                            </div>
                            <div className="flex flex-none items-center justify-end gap-2 self-center">
                                {matter.dmsLink && <DmsLink link={matter.dmsLink} isIconDisplayed={true} />}
                                {dashboardRowTrailing(matter) === 'blocked-tag' && <BlockedTag />}
                                {dashboardRowTrailing(matter) === 'actions' && (
                                    <MyCaseActionButtons matter={matter} canCloseCase={canCloseCase} />
                                )}
                            </div>
                        </div>
                    );
                })
            )}
            {currentData && !currentData.last ? (
                home ? (
                    <HomeShowMore onClick={handleLoadMore} loading={isFetching} />
                ) : (
                    <LoadMoreButton onClick={handleLoadMore} loading={isFetching} />
                )
            ) : null}
        </div>
    );
};

/** Home's Load more: a text button under a light rule. */
export const HomeShowMore = ({ onClick, loading }: { onClick: () => void; loading: boolean }) => (
    <div className="border-divider-soft flex justify-center border-t pt-1">
        <Button variant="ghost" size="sm" className="text-primary text-meta" onClick={onClick} disabled={loading}>
            {t('home.show-more')}
        </Button>
    </div>
);

/** Home's row: the mono number before the title, the DMS chip beside it, the client under it, plain icon actions. */
const HomeCaseRow = ({ matter, canCloseCase }: { matter: MatterListItem; canCloseCase: boolean }) => {
    const trailing = dashboardRowTrailing(matter);
    return (
        <div className="border-divider-soft flex flex-wrap items-start gap-x-3 gap-y-2 border-t py-2.5">
            <div className="flex min-w-0 flex-[1_1_12rem] flex-col gap-[3px]">
                {/* Below `sm` the title takes a line of its own under chips that leave it no room, rather than
                    shrinking to nothing beside them. The DMS chip shares the title's box, so it stays at the
                    title's right and the title wraps instead. */}
                <div className="flex items-start gap-x-1.5 gap-y-1 max-sm:flex-wrap">
                    <MatterIndicators isConfidential={matter.isConfidential} hasAccess={hasMatterAccess(matter)} />
                    <div className="flex min-w-0 items-start gap-x-1.5 max-sm:flex-[1_1_10rem]">
                        <RouterLink
                            to={`/matter/${matter.id}`}
                            className="text-foreground hover:text-link min-w-0 leading-[1.35] font-medium no-underline hover:underline max-sm:flex-1"
                        >
                            <LineClamp maxLineCount={2}>
                                <span className="text-grey-600 text-meta font-mono font-normal">
                                    <Unmasked>{matter.caseNumber}</Unmasked>
                                </span>{' '}
                                {matter.name}
                            </LineClamp>
                        </RouterLink>
                        {matter.dmsLink ? <DmsLink link={matter.dmsLink} variant="icon-chip" /> : null}
                    </div>
                </div>
                {matter.clientParty?.id ? (
                    // The client stays a link, drawn in the line's grey until hovered.
                    <span className="text-muted-foreground text-meta flex min-w-0 [&_a]:text-inherit">
                        <PartyName
                            party={matter.clientParty}
                            isLink
                            variant="readonly-other"
                            showPartyNumber
                            numberAfter
                            singleLine
                        />
                    </span>
                ) : null}
            </div>
            <div className="flex flex-none items-center gap-0.5">
                {trailing === 'blocked-tag' && <BlockedTag />}
                {trailing === 'actions' && <MyCaseActionButtons matter={matter} canCloseCase={canCloseCase} plain />}
            </div>
        </div>
    );
};

export const GridLoader = ({ height = 40, numberOfRows = 10 }: { height?: number; numberOfRows?: number }) => (
    <>
        {Array.from({ length: numberOfRows }, (_, i) => (
            // oxlint-disable-next-line react-doctor/no-array-index-as-key -- static skeleton loader rows have no domain id and never reorder
            <div className={cn(CASE_ROW, 'gap-2')} key={`row-${i}`}>
                <Skeleton className="flex-1 rounded-none" style={{ height }} />
                <Skeleton className="flex-1 rounded-none" style={{ height }} />
            </div>
        ))}
    </>
);

export default CasesTab;
